// src/admin/pages/brand/AddBrandDrawer.tsx
//
// Standalone "Add New Brand" drawer — same fields and validation as the
// add-mode drawer in BrandManagement.tsx, extracted so it can also be
// launched from ProductDrawer.tsx (quick-add while creating a product).
import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  TextField,
  Grid,
  Divider,
  Stack,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Tooltip,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { createBrand, fetchBrandStats, MAX_FEATURED_BRANDS } from './brandService';
import { useAuth } from '../../../contexts/AuthContext';

interface AddBrandDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (brandId: string) => void;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const EMPTY_FORM = { name: '', description: '', websiteUrl: '', isActive: true, featured: false };

const AddBrandDrawer: React.FC<AddBrandDrawerProps> = ({ open, onClose, onCreated, onShowToast }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [featuredCount, setFeaturedCount] = useState(0);

  useEffect(() => {
    if (open) {
      setFormData(EMPTY_FORM);
      setImageFile(null);
      setImagePreview('');
      fetchBrandStats().then(s => setFeaturedCount(s.featured)).catch(() => setFeaturedCount(0));
    }
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { onShowToast('Please select an image file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { onShowToast('Image size should be less than 5MB', 'error'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => { setImageFile(null); setImagePreview(''); };

  const handleClose = () => { if (saveLoading) return; onClose(); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) { onShowToast('You must be logged in to perform this action', 'error'); return; }
    if (!formData.name.trim()) { onShowToast('Please enter a brand name', 'error'); return; }
    if (!formData.description.trim()) { onShowToast('Please enter a description', 'error'); return; }
    if (!imageFile) { onShowToast('Please select an image', 'error'); return; }
    if (formData.featured && featuredCount >= MAX_FEATURED_BRANDS) {
      onShowToast(`Maximum ${MAX_FEATURED_BRANDS} brands can be featured. Please unfeature a brand first.`, 'warning');
      return;
    }

    setSaveLoading(true);
    try {
      const result = await createBrand(
        {
          name: formData.name,
          description: formData.description,
          websiteUrl: formData.websiteUrl,
          isActive: formData.isActive,
          featured: formData.featured,
          createdBy: currentUser.uid,
        },
        imageFile,
        currentUser.uid
      );
      if (result.success && result.brandId) {
        onShowToast('Brand created successfully', 'success');
        onCreated(result.brandId);
        onClose();
      } else {
        onShowToast(result.message || 'Failed to create brand', 'error');
      }
    } catch {
      onShowToast('Failed to save brand', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h6">Add New Brand</Typography>
            <IconButton onClick={handleClose} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <Box sx={{
              flex: 1, overflow: 'auto', px: 3, py: 3,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
            }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Brand Name" name="name" value={formData.name}
                    onChange={handleInputChange} required placeholder="Enter brand name" />
                </Grid>

                <Grid item xs={12}>
                  <TextField fullWidth label="Description" name="description" value={formData.description}
                    onChange={handleInputChange} required multiline rows={4}
                    placeholder="Enter brand description" />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Website URL (Optional)" name="websiteUrl"
                    value={formData.websiteUrl} onChange={handleInputChange}
                    type="url" placeholder="https://example.com"
                    InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }}
                  />
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">Brand Image</Typography>
                    <Typography variant="subtitle2" color="error">*</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Upload a brand logo (max 5MB, recommended: 800×800 px)
                  </Typography>
                  <Box sx={{ border: '1px dashed', borderColor: imagePreview ? 'divider' : 'error.main', borderRadius: 1, p: 2 }}>
                    {imagePreview ? (
                      <Box>
                        <Box
                          component="img"
                          src={imagePreview}
                          alt="Brand preview"
                          sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, mb: 1, bgcolor: 'grey.100', display: 'block' }}
                        />
                        <Button onClick={handleRemoveImage} variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} fullWidth>
                          Remove Image
                        </Button>
                      </Box>
                    ) : (
                      <Button component="label" variant="outlined" color="error" startIcon={<CloudUploadIcon />} fullWidth>
                        Upload Brand Image
                        <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                      </Button>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch name="isActive" checked={formData.isActive} onChange={handleInputChange} color="success" />}
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Tooltip
                    title={
                      formData.featured
                        ? ''
                        : featuredCount >= MAX_FEATURED_BRANDS
                          ? `Maximum ${MAX_FEATURED_BRANDS} featured brands reached`
                          : ''
                    }
                    disableHoverListener={formData.featured || featuredCount < MAX_FEATURED_BRANDS}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          name="featured"
                          checked={formData.featured}
                          onChange={handleInputChange}
                          color="warning"
                          disabled={!formData.featured && featuredCount >= MAX_FEATURED_BRANDS}
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>Featured</span>
                          {featuredCount >= MAX_FEATURED_BRANDS && !formData.featured && (
                            <Typography variant="caption" color="error">(limit reached)</Typography>
                          )}
                        </Stack>
                      }
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>

            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleClose} disabled={saveLoading} startIcon={<CancelIcon />}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saveLoading ? 'Creating...' : 'Create Brand'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Backdrop
        open={saveLoading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, minWidth: 280, boxShadow: 24 }}>
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Creating Brand…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default AddBrandDrawer;
